import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "../components/shared/Breadcrumb";
import { DesignCard } from "../components/shared/DesignCard";
import { PageMeta } from "../components/shared/PageMeta";
import { ButtonLink } from "../components/shared/Button";
import { CheckIcon } from "lucide-react";
import { designs, frontageGuides, standardInclusions, designCustomisations } from "../data/designs";

// Catalogue → Bricks query loop on the "design" CPT with native filters (frontage, bedrooms).
// Bathrooms, car spaces and storeys are identical across all records, so those filters are hidden.
type FrontageFilter = "all" | "10m" | "12.5m" | "15m";
type BedroomFilter = "any" | "3" | "4";
const frontageOptions: {
  value: FrontageFilter;
  label: string;
}[] = [{
  value: "all",
  label: "All"
}, {
  value: "10m",
  label: "10m"
}, {
  value: "12.5m",
  label: "12.5m"
}, {
  value: "15m",
  label: "15m"
}];
const bedroomOptions: {
  value: BedroomFilter;
  label: string;
}[] = [{
  value: "any",
  label: "Any"
}, {
  value: "3",
  label: "3 bed"
}, {
  value: "4",
  label: "4 bed"
}];
export function HomeDesigns() {
  const [frontage, setFrontage] = useState<FrontageFilter>("all");
  const [bedrooms, setBedrooms] = useState<BedroomFilter>("any");
  const results = useMemo(() => designs.filter(design => (frontage === "all" || design.frontage === frontage) && (bedrooms === "any" || String(design.bedrooms) === bedrooms)), [frontage, bedrooms]);
  const filtered = frontage !== "all" || bedrooms !== "any";
  const reset = () => {
    setFrontage("all");
    setBedrooms("any");
  };
  const segment = (selected: boolean) => `min-h-[44px] whitespace-nowrap px-5 text-[15px] font-semibold transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean ${selected ? "bg-charcoal text-paper" : "bg-white text-charcoal hover:bg-paper"}`;
  return <>
      <PageMeta title="Single-Storey Home Designs Perth | Oceanside Living" description="Compare six single-storey home designs for 10m, 12.5m and 15m lots in Perth. Stone benchtops, Westinghouse appliances and 30-course ceilings as standard." data-id="element-727" data-mp="om7vobhpldza" />
      <div className="mx-auto max-w-site px-5 pb-16 pt-10 md:px-10 md:pb-28 md:pt-14" data-id="element-728" data-mp="aujngjakz9nl">
        <Breadcrumb items={[{
        label: "Home",
        to: "/"
      }, {
        label: "Home designs"
      }]} data-id="element-729" data-mp="asqcwswuk5ab" />
        <h1 className="mt-6 text-[38px] font-semibold leading-[1.08] tracking-tight md:text-[60px]" data-id="element-730" data-mp="17k0y7cof7lw5">Home designs</h1>
        <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-slate md:text-lg" data-id="element-731" data-mp="12jwkfuewd59m">
          Six single-storey designs grouped by the lot width they suit: 10m, 12.5m and 15m. Your frontage is the quickest way to
          narrow the range. Every design can be tailored, and if none quite fit, we design fully custom homes.
        </p>

        <div className="mt-10 flex flex-col gap-6 border-y border-line py-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-732" data-mp="3ea8kr0cmdcaq">
          <div className="flex flex-col gap-5 sm:flex-row sm:gap-10" data-id="element-733" data-mp="1tusnz4tp7h53">
            <fieldset data-id="element-734" data-mp="1yriyzdqnbmjn">
              <legend className="mb-2 text-sm font-semibold" data-id="element-735" data-mp="1ysu3jfrx0ybt">Lot width</legend>
              <div className="inline-flex divide-x divide-line overflow-hidden rounded-full border border-line" data-id="element-736" data-mp="22sut9wkgsvy6">
                {frontageOptions.map(option => <button key={option.value} type="button" aria-pressed={frontage === option.value} onClick={() => setFrontage(option.value)} className={segment(frontage === option.value)} data-id="element-737" data-mp="2cjz8mxe1yitj">
                    {option.label}
                  </button>)}
              </div>
            </fieldset>
            <fieldset data-id="element-738" data-mp="2qk08c9ikzk7f">
              <legend className="mb-2 text-sm font-semibold" data-id="element-739" data-mp="2qigpcb5jiv1y">Bedrooms</legend>
              <div className="inline-flex divide-x divide-line overflow-hidden rounded-full border border-line" data-id="element-740" data-mp="s4dunxqxs8ui">
                {bedroomOptions.map(option => <button key={option.value} type="button" aria-pressed={bedrooms === option.value} onClick={() => setBedrooms(option.value)} className={segment(bedrooms === option.value)} data-id="element-741" data-mp="w7aefrs7qb01">
                    {option.label}
                  </button>)}
              </div>
            </fieldset>
          </div>
          <div className="flex items-center gap-5" data-id="element-742" data-mp="5tvoc2fhi6by">
            <p role="status" aria-live="polite" className="text-[15px] text-slate" data-id="element-743" data-mp="5v6ols9q0vyb">
              Showing <span className="font-semibold text-charcoal" data-id="element-744" data-mp="3eagoquu1vr3g">{results.length}</span> of {designs.length} designs
            </p>
            {filtered && <button type="button" onClick={reset} className="text-[15px] font-medium underline underline-offset-4" data-id="element-745" data-mp="3o0ifxvhp9pml">
                Reset filters
              </button>}
          </div>
        </div>

        {results.length > 0 ? <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-746" data-mp="2vhuyqr5tquqs">
            {results.map(design => <DesignCard key={design.slug} design={design} headingLevel="h2" data-id="element-747" data-mp="2qis29v3wtwuf" />)}
          </div> : <div className="mt-10 rounded-[28px] border border-line bg-white p-8 text-center md:p-12" data-id="element-748" data-mp="1at0nq9z4jrle">
            <h2 className="text-2xl font-semibold" data-id="element-749" data-mp="x25hl5g0foli">No designs match those filters</h2>
            <p className="mx-auto mt-3 max-w-xl text-[17px] text-slate" data-id="element-750" data-mp="113h4323l7tph">
              Try a different combination, or tell us about your block and what you need. A custom home may suit you better.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-751" data-mp="1tsob6ph3xrmc">
              <button type="button" onClick={reset} className="inline-flex min-h-[52px] items-center rounded-full border-[1.5px] border-charcoal px-6 text-[15px] font-semibold" data-id="element-752" data-mp="1tpsbowpm2uvs">
                Reset filters
              </button>
              <ButtonLink to="/contact?project=custom" data-id="element-753" data-mp="4covepizh3xq">Discuss a custom home</ButtonLink>
            </div>
          </div>}

        <section aria-labelledby="standard-heading" className="mt-16 grid gap-6 lg:grid-cols-2" data-id="element-754" data-mp="9d2wtoq2xfms">
          <div className="rounded-[28px] bg-charcoal p-7 text-paper md:p-10" data-id="element-755" data-mp="2b05rv2dn384l">
            <h2 id="standard-heading" className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-756" data-mp="262478d0kf0r0">What every Oceanside home includes</h2>
            <p className="mt-2 text-[16px] text-paper/70" data-id="element-757" data-mp="219ku1h5si7mq">Standard in every design in our range. Upgrades and changes are available.</p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-758" data-mp="1sbilmgjq8cpf">
              {standardInclusions.map(item => <li key={item} className="flex items-start gap-3 text-[15px]" data-id="element-759" data-mp="1nbmrra4qu1nl">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-760" data-mp="1icjljmd11pe1"><CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-761" data-mp="1ebpwgpnum4jk" /></span>
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="rounded-[28px] border border-line bg-white p-7 md:p-10" data-id="element-762" data-mp="3cohvxrn2orbi">
            <h2 className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-763" data-mp="38n4q3hjzco4l">Make it your own</h2>
            <p className="mt-2 text-[16px] text-slate" data-id="element-764" data-mp="ufdkurvr99ep">Our designs can be altered to suit you:</p>
            <ul className="mt-6 space-y-3" data-id="element-765" data-mp="zegelg6abt7d">
              {designCustomisations.map(item => <li key={item} className="flex items-start gap-3 text-[15px]" data-id="element-766" data-mp="149dvtadpx8vc">
                  <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-ocean" data-id="element-767" data-mp="188d1ix79x9nu" />
                  {item}
                </li>)}
            </ul>
          </div>
        </section>

        <div className="mt-6 grid gap-6 lg:grid-cols-3" data-id="element-768" data-mp="ghyot4azy3dr">
          {frontageGuides.map(guide => <section key={guide.frontage} aria-labelledby={`guide-${guide.frontage}`} className="rounded-[28px] border border-line bg-white p-7 md:p-10" data-id="element-769" data-mp="klelu1qza5qz">
              <span className="rounded-full bg-charcoal px-3 py-1 text-[13px] font-semibold text-paper" data-id="element-770" data-mp="1vz8ptkcitmqc">{guide.frontage} lot</span>
              <h2 id={`guide-${guide.frontage}`} className="mt-5 text-[24px] font-semibold tracking-tight md:text-[28px]" data-id="element-771" data-mp="20vpau5ofbkz7">{guide.title}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-772" data-mp="17m5bdt4xbdff">{guide.intro}</p>
              <dl className="mt-6 space-y-4 border-t border-line pt-6" data-id="element-773" data-mp="1lda2u7y50zeo">
                {guide.points.map(point => <div key={point.title} data-id="element-774" data-mp="1gm1tfbwt5e1i">
                    <dt className="font-semibold" data-id="element-775" data-mp="1gla9wzghj6rl">{point.title}</dt>
                    <dd className="mt-1 text-[15px] leading-relaxed text-slate" data-id="element-776" data-mp="1vaohq34exg4v">{point.text}</dd>
                  </div>)}
              </dl>
            </section>)}
        </div>

        <div className="mt-16 grid gap-8 border-t border-charcoal pt-10 lg:grid-cols-12" data-id="element-777" data-mp="24b4kfadc0j8o">
          <div className="lg:col-span-7" data-id="element-778" data-mp="20aayunnd3giy">
            <h2 className="text-[26px] font-semibold tracking-tight md:text-[32px]" data-id="element-779" data-mp="20bcc8yruof3u">Not sure where to start?</h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate" data-id="element-780" data-mp="9zy7ymkcvr4n">
              The best design starts with your block, not the brochure. Send us your lot details and we’ll recommend the designs
              that fit, after checking setbacks, site coverage and any estate guidelines for you.
            </p>
          </div>
          <div className="flex flex-col gap-4 lg:col-span-5 lg:items-end lg:justify-end" data-id="element-781" data-mp="3i8pg3i55kzy2">
            <ButtonLink to="/contact?project=unsure" data-id="element-782" data-mp="3n70p5qnmj6f8">Send us your lot details</ButtonLink>
            <Link to="/#services" className="text-[15px] font-medium underline underline-offset-4" data-id="element-783" data-mp="389zvvipsb3en">See everything we build</Link>
          </div>
        </div>
      </div>
    </>;
}